import React, { ChangeEvent, useEffect, useState } from 'react'
import {
  Button,
  ButtonType,
  Card,
  CardContainer,
  CardContent,
  CardSizeTypes,
  Checkbox,
  CheckboxChangeEvent,
  Form,
  FormFieldSize,
  FormGroup,
  Input,
  Select,
  SubmitButton
} from '@digicert/dcone-common-ui'
import { ExtendedCreateAccountProps } from "./CreateAccount.types"
import { mapOptionsCommonUi, serviceIcon, translate } from 'shared/helpers/utils'
import { Translate } from 'react-localize-redux'
import { NAME_REGEXP } from 'core/utils/constants'
import createAccountStyles from './CreateAccount.module.scss'
import { ISort } from "../../core/actions/Actions.interfaces";
import { BasePathConst } from "../../configs/Path.configs";
import history from 'core/history/history'
import { createCustomer } from "../../core/actions/Accounts.actions";
import { connect, useDispatch } from "react-redux";
import { IAppState } from "../../core/store/store";
import { fetchTenants } from "../tenants/Tenants.actions";
import cn from "classnames";
import { IApplication } from "../../reducers/Reducers.interfaces";
import { fetchProfile } from '../../core/actions/Profile.actions';

const CreateAccount = (
    {
      services,
      createCustomer,
      fetchTenants,
      localeList,
      onCancel
    }:ExtendedCreateAccountProps) => {
  const dispatch = useDispatch();
  const [accountName, setAccountName] = useState('')
  const [nameMaxLengthError, setNameMaxLengthError] = useState(false)
  const [language, setLanguage] = useState('en_US')
  const [selectedServices, setSelectedServices] = useState<IApplication[]>([])
  const [createDconeAccount, setCreateDconeAccount] = useState<boolean>(false)
  const [showRecommendations, setShowRecommendations] = useState(false)

  useEffect(() => {
    setAccountName('')
    setNameMaxLengthError(false)
    setLanguage('en_US')
    setSelectedServices([])
    setCreateDconeAccount(false)
    setShowRecommendations(false)
  }, [onCancel])

  const onAccountNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAccountName(event.target.value)
    if (event.target.value.length > 255) {
      setNameMaxLengthError(true)
    } else {
      setNameMaxLengthError(false)
    }
  }

  const validateName = () => {
    if (nameMaxLengthError) {
      return translate('notifications.errors.maxLengthError') as string
    }
  }

  const validateLangSelect = (): any => !language ? translate('common.form.validation.required') : ''

  const onLangChange = (lang): void => {
    setLanguage(lang)
  }

  const handleCancel = (e) => {
    e.preventDefault()
    if(onCancel) {
      onCancel()
    }
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const payload = {
      'account_name': accountName,
      locale: language,
      applications: selectedServices.map(service => service.id),
      instant_account_provisioning: createDconeAccount
    }

    createCustomer(payload).then(() => {
      const params: ISort = { limit: 1, offset: 0 }
      const nameOfAccount = { name: payload.account_name }
      fetchTenants(params, nameOfAccount).then((response) => {
        history.push(`${BasePathConst}/accounts/${response.items[0]?.id}`)
      })
      dispatch(fetchProfile());
    })
  }

  const selectService = (e: MouseEvent, item: IApplication): void => {
    e.preventDefault()
    let tmpArray:Array<IApplication> = [...selectedServices]
    const serviceExists = selectedServices.some(service => service.id === item.id)

    if(!serviceExists) {
      tmpArray.push(item)
      setSelectedServices(tmpArray)
    }

    else {
      let filterService = tmpArray.filter(service => service.id !== item.id)
      setSelectedServices(filterService)
    }
  }

  useEffect(() => {
    if (selectedServices.some(service => service.create_dcone_account)) {
      setShowRecommendations(true)
      setCreateDconeAccount(true)
    } else {
      setShowRecommendations(false)
      setCreateDconeAccount(false)
    }
  }, [selectedServices]);

  return (
      <section className={createAccountStyles.createAccount}>
        <h3>
          <Translate id='notifications.accounts.createAccountTitle' />
        </h3>

      <Form center={false} onSubmit={(e) => {onSubmit(e)}}>
        <FormGroup>
          <Input
            size={FormFieldSize.L}
            label={translate('createAccount.accountName')}
            required
            minLength={1}
            maxLength={256}
            value={accountName}
            onChange={onAccountNameChange}
            errors={{
              valueMissing: translate('common.form.validation.required')
            }}
            id='account_name'
            validate={validateName}
          />
        </FormGroup>

          <FormGroup colWidth={FormFieldSize.XL}>
            <Select
                getPopupContainer={triggerNode => triggerNode.parentElement}
                allowClear
                defaultValue={'English'}
                label={translate('profile.language')}
                onChange={onLangChange}
                options={mapOptionsCommonUi(localeList?.locales)}
                validate={validateLangSelect}
                value={language}
                id='account-language'
            />
          </FormGroup>

          <div className={createAccountStyles.services}>
            <h3><Translate id='accounts.availableServices'/></h3>
            <p><Translate id='accounts.serviceSelectionDescription'/></p>
            <CardContainer className={createAccountStyles.cardContainer}>
              {services.map((item, index) => {
                return (
                    <Card
                        key={index}
                        className={createAccountStyles.card}
                        size={CardSizeTypes.SMALL}
                        onClick={(e: MouseEvent) => selectService(e, item)}
                        content={
                          <CardContent>
                            <div className={createAccountStyles.cardContent} key={index}>
                              <div>{serviceIcon(item.name)}</div>
                              <div className={createAccountStyles.cardLabel}>{item.name}</div>
                              <div>
                                <Checkbox checked={selectedServices.some(service => service.id === item.id)}/>
                              </div>
                            </div>
                          </CardContent>}
                    />
                )
              })}
            </CardContainer>
          </div>

          <div className={cn(createAccountStyles.recommendations, !showRecommendations && createAccountStyles.hide)}>
            <Checkbox
                className={createAccountStyles.checkGroup}
                onChange={(e: CheckboxChangeEvent) => {setCreateDconeAccount(e.checked)}}
                checked={createDconeAccount}
                label={translate('accounts.createDconeAccount') as string}
            />
            <p className={createAccountStyles.desc}><Translate id='accounts.recommend'/></p>
          </div>

          <div className={createAccountStyles.formActions}>
            <Button buttonType={ButtonType.SECONDARY} onClick={handleCancel}>
              <Translate id='common.form.buttons.cancelBtn' />
            </Button>

            <SubmitButton disabled={selectedServices.length === 0}>
              <Translate id='businessUnits.wizardCreateBtn'/>
            </SubmitButton>
          </div>
        </Form>
      </section>
  )
}

export default connect(
    (state: IAppState) => ({
      profile: state.profile?.data,
    }),
    { fetchTenants, createCustomer }
)(CreateAccount)
